import React, { useMemo } from 'react';
import { createContext, useContext, useReducer } from 'react';
import { Action } from '../../types';
import { MedisanteDevice } from '../../pages/Device/types';
import { AuthUser, Org } from '../AuthProvider/types';
import { Direction, SortState } from '../../utils/sort';
import { ListDecoratedMedisanteDevice } from '../../pages/Devices/types';
import { MedisanteTargetSystem } from '../../pages/TargetSystems/types';
import { MedisanteOrgReport } from '../../pages/Dashboard/types';

export interface IManageContextState {
  isEmbeddedApp?: boolean;
  lastChange?: Date;
  isAppSearchVisible?: boolean;
  scannedQuery?: string;
  searchQuery?: string[];
  advancedSearchFilters?: Filters;
  advancedSearchQuery?: string;
  advancedSearchAlert?: boolean;
  searchError?: string;
  lastQueryChange?: number;
  devices?: any;
  loadingDevices?: boolean;
  isSidebarVisible?: boolean;
  user?: AuthUser | null;
  authenticated?: boolean;
  barcode?: {
    visible?: boolean;
    value?: string | null;
  };
  timestamp?: number;
  version?: string | null;
  selectedOrgs?: Org[] | null;
  isTransferMessageVisible?: boolean;
  page?: string;
  isOrgSelectionVisible?: boolean;
  userOrgs?: Org[];
  organizations?: {
    [id: string]: string;
  };
  selectedDevice?: MedisanteDevice;
  sortStates: {
    devicesSortState: SortState<ListDecoratedMedisanteDevice>;
    targetSystemsSortState: SortState<MedisanteTargetSystem>;
  };
  updateDeviceQueue?: { [id: string]: string } | null;
  reports?: MedisanteOrgReport[];
}

interface IManageContext {
  state: IManageContextState;
  dispatch: (action: Action) => void;
  toggleDarkMode: () => void;
  isDarkModeActive: boolean;
}
const indexOrganizations = (action: Action): any => {
  const index: { [id: string]: string } = {};

  action.data.orgs.forEach((item: any) => {
    const key: string = item.id;
    index[key] = item.name;
  });

  return index;
};

export const reducer = (
  state: IManageContextState,
  action: Action
): IManageContextState => {
  if (process.env.NODE_ENV === 'development') {
    console.log(action.type.toUpperCase(), state, action);
  }

  switch (action.type) {
    case 'embedded-app':
      return { ...state, isEmbeddedApp: true };
    case 'transfer-ownership':
      return { ...state, isTransferMessageVisible: action.data };
    case 'search-started':
      return { ...state, isAppSearchVisible: true };
    case 'search-closed':
      return { ...state, isAppSearchVisible: false };
    case 'search-query-changed':
      return {
        ...state,
        searchQuery: action.data,
        loadingDevices: action.data !== null && action.data.length > 0,
        devices: [],
      };
    case 'advanced-search-query-changed':
      return {
        ...state,
        advancedSearchFilters: action.data.filters,
        advancedSearchQuery: action.data.query,
        advancedSearchAlert: action.data.alert,
        searchError: action.data.error,
        loadingDevices:
          action.data.filters !== null || action.data.query !== null,
        devices: [],
      };
    case 'scanned-query-changed':
      return {
        ...state,
        scannedQuery: action.data,
      };
    case 'reset-scanned-query':
      return {
        ...state,
        scannedQuery: '',
        barcode: {
          visible: false,
          value: null,
        },
      };
    case 'device-list-changed':
      return { ...state, devices: action.data, loadingDevices: false };
    case 'device-selected':
      return {
        ...state,
        selectedDevice: action.data,
        isAppSearchVisible: false,
      };
    case 'sidebar-changed':
      return { ...state, isSidebarVisible: !state.isSidebarVisible };
    case 'sidebar-hidden':
      return { ...state, isSidebarVisible: false };
    case 'org-selection':
      return {
        ...state,
        isOrgSelectionVisible: action.data.visible,
        isSidebarVisible: false,
      };
    case 'selected-org-changed':
      localStorage.setItem('refresh', '1');
      return {
        ...state,
        selectedOrgs: !!state.userOrgs
          ? state.userOrgs.filter((item: any) =>
              action.data.some((i: any) => i.id === item.id)
            )
          : null,
        isOrgSelectionVisible: false,
        user: state.user
          ? {
              ...state.user,
              selectedOrgs: (action.data as Org[]).map((o) => o.id),
            }
          : null,
        updateDeviceQueue: null,
      };
    case 'user-changed':
      const userSelectedOrgs = action.data.selectedOrgs;
      return {
        ...state,
        user: action.data,
        organizations: indexOrganizations(action),
        userOrgs: action.data.orgs,
        selectedOrgs: action.data.orgs.filter((item: any) =>
          userSelectedOrgs.includes(item.id)
        ),
        authenticated: true,
      };
    case 'page-changed':
      return { ...state, page: action.data };
    case 'barcode-changed': {
      const bc = action.data.value;

      return {
        ...state,
        scannedQuery: bc,
        barcode: {
          ...action.data,
        },
      };
    }
    case 'target-systems-changed':
      return { ...state, timestamp: Date.now() };
    case 'version-changed':
      return { ...state, version: action.data.version };
    case 'updating-device-state': {
      let queue: { [id: string]: string } = {};

      if (state.updateDeviceQueue) {
        queue = { ...state.updateDeviceQueue };
      }

      const newDeviceState = action.data.newState;

      action.data.deviceIDs.forEach((item: string) => {
        queue[item] = newDeviceState;
      });

      return { ...state, updateDeviceQueue: queue };
    }
    case 'device-status-changed':
      return { ...state, updateDeviceQueue: action.data };
    case 'change-devices-sort-state':
      return {
        ...state,
        sortStates: { ...state.sortStates, devicesSortState: action.data },
      };
    case 'change-target-systems-sort-state':
      return {
        ...state,
        sortStates: {
          ...state.sortStates,
          targetSystemsSortState: action.data,
        },
      };
    case 'reports-changed':
      return { ...state, reports: action.data };
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('WARNING', 'Unknown action', action);
  }

  return { ...state };
};

const ManageContext = createContext<IManageContext>({
  state: {
    isEmbeddedApp: false,
    isAppSearchVisible: false,
    sortStates: {
      devicesSortState: {},
      targetSystemsSortState: {},
    },
  },
  dispatch: (action: Action): void => {},
  toggleDarkMode: () => {},
  isDarkModeActive: false,
});

export const initialManageContextState: IManageContextState = {
  isEmbeddedApp: false,
  isAppSearchVisible: false,
  scannedQuery: '',
  lastQueryChange: Date.now(),
  devices: [],
  loadingDevices: false,
  isSidebarVisible: false,
  user: null,
  authenticated: false,
  barcode: {
    visible: false,
    value: null,
  },
  timestamp: 0,
  version: null,
  sortStates: {
    devicesSortState: {
      lastMeasurementAt: 'desc' as Direction,
    },
    targetSystemsSortState: {},
  },
  reports: [],
};

export const ManageContexWrapper = (props: React.PropsWithChildren<any>) => {
  const { toggleDarkMode, isDarkModeActive } = props;
  const [state, dispatch] = useReducer(reducer, {
    ...initialManageContextState,
  });

  const value = useMemo(
    () => ({
      state,
      dispatch,
      toggleDarkMode,
      isDarkModeActive,
    }),
    [state, dispatch, toggleDarkMode, isDarkModeActive]
  );

  return (
    <ManageContext.Provider value={value}>
      {props.children}
    </ManageContext.Provider>
  );
};

export const ManageContextProvider = ManageContext.Provider;
export const useManageContext = () => useContext(ManageContext);
